import { isEmpty } from 'lodash-es';
import { ApiError, CRMError, ErrorMessage, FieldError } from '../@types';
import { useToast } from '../contexts/ToastContext';
import { useCallback } from 'react';

export const useToastErrorMessage = () => {
  const { enqueueSnackbar } = useToast();

  const handleDefaultError = useCallback(() => {
    enqueueSnackbar({ message: 'Something went wrong.', variant: 'error' });
  }, [enqueueSnackbar]);

  const handleStringError = useCallback(
    (errorMessage: string) => {
      if (!isEmpty(errorMessage)) {
        enqueueSnackbar({ message: errorMessage, variant: 'error' });
      } else {
        handleDefaultError();
      }
    },
    [enqueueSnackbar, handleDefaultError]
  );

  const handleObjectError = useCallback(
    (errorMessage: Exclude<ErrorMessage, string>) => {
      if (errorMessage && errorMessage.message) {
        enqueueSnackbar({ message: errorMessage.message, variant: 'error' });
      } else {
        handleDefaultError();
      }
    },
    [enqueueSnackbar, handleDefaultError]
  );

  const handleErrorsArray = useCallback(
    (errors: (FieldError | CRMError | string)[]) => {
      errors.forEach((errorItem) => {
        let errorMessage: string;

        if (typeof errorItem === 'string') {
          errorMessage = errorItem;
        } else if ('message' in errorItem) {
          errorMessage = errorItem.message;
        } else {
          errorMessage = errorItem.error;
        }

        enqueueSnackbar({ message: errorMessage, variant: 'error' });
      });
    },
    [enqueueSnackbar]
  );

  const handleErrorMessage = useCallback(
    (error: ApiError) => {
      const errorData = error?.response?.data;

      if (!errorData) {
        handleDefaultError();
      } else if (typeof errorData.errorMessage === 'string') {
        handleStringError(errorData.errorMessage);
      } else if (typeof errorData.errorMessage === 'object') {
        handleObjectError(errorData.errorMessage);
      } else if (Array.isArray(errorData.errors) && errorData.errors.length > 0) {
        handleErrorsArray(errorData.errors);
      } else if (typeof errorData.message === 'string') {
        handleStringError(errorData.message);
      } else if (typeof errorData.message === 'object' && !Array.isArray(errorData.message)) {
        handleObjectError(errorData.message);
      } else if (Array.isArray(errorData.message) && errorData.message.length > 0) {
        handleErrorsArray(errorData.message);
      } else {
        handleDefaultError();
      }
    },
    [handleDefaultError, handleErrorsArray, handleObjectError, handleStringError]
  );

  return {
    handleErrorMessage,
  };
};
